import { Box, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { parentManagePaymentMethodPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import { usePaymentMethods } from "../../hooks/CurrentUser";

export function shouldShowBackupPaymentBanner({
  currentUser,
  paymentMethods,
}: {
  currentUser: ReturnType<typeof useSession>["currentUser"];
  paymentMethods: ReturnType<typeof usePaymentMethods>["paymentMethods"];
}) {
  const hasBackupPaymentMethod = paymentMethods.some(
    paymentMethod => !!paymentMethod.isBackUpForOffSessionCharges,
  );
  if (hasBackupPaymentMethod) {
    return false;
  }

  if (paymentMethods.length === 0 && !currentUser?.hasSubscription) {
    return false;
  }

  return true;
}

export default function BackupPaymentBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\BackupPaymentBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();
  const { currentUser } = useSession();

  const { paymentMethods, loading: paymentMethodsLoading } =
    usePaymentMethods();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  // User has no payment methods but has an active subscription
  const needsPaymentMethod =
    paymentMethods.length === 0 && !!currentUser?.hasSubscription;

  const shouldShowBanner = React.useMemo(
    () =>
      !paymentMethodsLoading &&
      shouldShowBackupPaymentBanner({
        currentUser,
        paymentMethods,
      }),
    [currentUser, paymentMethods, paymentMethodsLoading],
  );
  useEffect(() => {
    if (!hasTrackedViewRef.current && shouldShowBanner) {
      hasTrackedViewRef.current = true;
      trackEvent("backup_payment_banner_view", {
        is_mobile: isMobile,
        needs_payment_method: needsPaymentMethod,
      });
    }
  }, [trackEvent, isMobile, shouldShowBanner, needsPaymentMethod]);

  const handleBannerClick = () => {
    trackEvent("backup_payment_banner_click", {
      is_mobile: isMobile,
      needs_payment_method: needsPaymentMethod,
    });
    navigate(parentManagePaymentMethodPath());
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: "primary.600",
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {needsPaymentMethod ? (
                <Trans t={t}>
                  <b>Never miss a class!</b> Add a payment method to keep your
                  membership active.
                </Trans>
              ) : (
                <Trans t={t}>
                  <b>Never miss a class!</b> If your default card fails, we’ll
                  automatically try your backup.
                </Trans>
              )}
              <Box
                component="span"
                sx={{
                  marginLeft: 8,
                  textDecoration: "underline",
                }}
              >
                {needsPaymentMethod ? t("Add a Card") : t("Add a Backup Card")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
